import React from "react";
import { useFormik } from "formik";
import useRoute from "@hooks/useNextRoute";
import _ from "lodash";
import { DEFAULT_FILTER, getFilterValues } from "./helpers";
import { Pagination } from "@type/common/Pagination";
import { getApiLocale } from "@translations/util";
import qs from "qs";

interface TypeFormikConfig {
  enableReinitialize: boolean;
  initialValues: TypeFormikValue;
  onSubmit: (values: TypeFormikValue, actions: any) => void;
}
interface TypeFormikValue {
  page?: number;
  filters?: [];
  searchs?: [];
}
export interface TypeUseFilter {
  (config?: any): TypeUseFilterReturn;
}
export interface TypeUseFilterReturn {
  values: any;
  getPageQuery: () => number;
  setPageQuery: (val: number) => void;
  getFiltersQuery: () => any;
  getSortersQuery: () => any;
  getQuery: (opts?: any) => object;
  getUrlQuery: (opts?: any) => string;
  getPaginationQuery: (opts?: any) => any;
  changeFilter: (opts?: any) => void;
  changeSearch: (key: string, value: any) => void;
  resetPage: () => void;
}
export interface TypeUseFilterForm {
  (config?: any, paramsUrl?: string[]): TypeUseFilterReturn;
  SORTER_MAPPING: any;
  getFilteredValues: () => [];
  getSortOrder: (column: string) => string | false;
}
const useFilter: TypeUseFilter = (params: any, paramsUrl?: string[]) => {
  const route = useRoute();
  const initialValues: TypeFormikValue = React.useMemo(() => {
    return getFilterValues({ ...useRoute.getParams(), ...params });
  }, []);
  // console.log({ initialValues, params, getParams: useRoute.getParams() });
  const config: TypeFormikConfig = {
    enableReinitialize: true,
    initialValues,
    onSubmit: () => {},
  };
  const form: any = useFormik(config);
  _.assign(form, {
    getFilterParams: () => {
      const result: any = {
        // page number
        ...(form.values.q ? { q: form.values.q } : {}),
        ...(form.values.page >= 1 ? { page: form.values.page } : {}),
      };
      const fields = _.compact([
        "sorters",
        "searchs",
        "filters",
        ..._.castArray(paramsUrl),
      ]);

      _.map(fields, (field) => {
        if (!_.isEmpty(form.values[field]) || _.isNumber(form.values[field])) {
          result[field] = JSON.stringify(form.values[field]);
        }
      });

      return result;
    },
    changeFilter: ({
      pagination,
      filters,
      sorters,
    }: {
      pagination?: Pagination;
      filters?: object;
      sorters?: string[];
    }) => {
      if (!_.isEmpty(sorters)) {
        _.set(form, "values.sorters", sorters);
      }
      if (!_.isEmpty(filters)) {
        filters = _.map(filters, (value) => {
          let rtn;
          const toFilterQuery = DEFAULT_FILTER;
          if (_.isFunction(toFilterQuery)) {
            rtn = toFilterQuery(value);
          }
          return rtn;
        });
        _.set(form, "values.filters", filters);
      }
      if (!_.isEmpty(pagination)) {
        _.set(form, "values.page", _.get(pagination, "current"));
        _.set(form, "values.pageSize", _.get(pagination, "pageSize"));
      }
      route.pushStateParams(form.getFilterParams());
    },
    changeSearch: (key: string, value: any) => {
      // TODO khi cần search theo từ khóa sẽ làm sau;
      let filters = _.get(form, "values.filters");
      if (_.findIndex(filters, (item) => _.has(item, key)) !== -1) {
        filters = _.compact(
          _.map(filters, (filter) => {
            if (_.has(filter, key)) {
              if (_.isEmpty(value) && !_.isNumber(value)) {
                return null;
              } else {
                return { [key]: value };
              }
            }
            return filter;
          })
        );
      } else {
        filters.push({ [key]: value });
      }
      _.set(form, "values.filters", filters);
      route.pushStateParams(form.getFilterParams());
    },
    getFiltersQuery: () => {
      const filters = _.get(form, "values.filters", {});
      const rtn = {};
      if (!_.isEmpty(filters)) {
        _.assign(rtn, filters);
      }
      return rtn;
    },
    resetPage: () => {
      _.set(form, "values.page", 1);
      route.pushStateParams(form.getFilterParams());
    },
    getSortersQuery: (defaultSorter: object) => {
      const sorters = _.get(form, "values.sorters", {});
      let rtn = defaultSorter || {};
      if (!_.isEmpty(sorters)) {
        rtn = { ...sorters, ...rtn };
      }
      if (_.isEmpty(rtn)) rtn = { createdAt: "desc" };
      return rtn;
    },

    getPaginationQuery: (opts: { page?: number; pageSize?: number }) => {
      const page = _.get(opts, "page");
      const pageSize = _.get(opts, "pageSize");
      return {
        page: !_.isUndefined(page) ? page : _.get(form, "values.page"),
        pageSize: !_.isUndefined(pageSize)
          ? pageSize
          : _.get(form, "values.pageSize"),
      };
    },
    getPageQuery: (defVal: number) => {
      return _.get(form, "values.page") || defVal || 1;
    },
    setPageQuery: (defVal: number) => {
      return _.set(form, "values.page", defVal || 1);
    },
    getQuery: () => {
      return form.values;
    },
    getUrlQuery: (config?: {
      filters?: any;
      populate?: any;
      locale?: any;
      page?: number;
      pageSize?: number;
      opts?: [];
    }) => {
      const populate = _.get(config, "populate", []);
      const locale = _.get(config, "locale", "en");
      // Map locale to API locale format (zh -> zh-Hans, zh-HK -> zh-Hant)
      const apiLocale = getApiLocale(locale);
      const filters = {
        ...form.getFiltersQuery(),
        ..._.get(config, "filters", {}),
      };
      const sort = _.map(form.getSortersQuery(), (value, key) => {
        let result = "";
        if (value) {
          result = `${key}:${value}`;
        }
        return result;
      });
      const pagination = form.getPaginationQuery({
        page: _.get(config, "page"),
        pageSize: _.get(config, "pageSize"),
      });
      return decodeURIComponent(
        `?${qs.stringify(
          _.pick(
            {
              ..._.get(form, "values"),
              filters,
              sort,
              pagination,
              populate,
              locale: apiLocale,
            },
            _.get(
              config,
              "opts",
              _.compact([
                "pagination",
                "sort",
                "filters",
                "populate",
                "locale",
                ..._.castArray(paramsUrl),
              ])
            )
          )
        )}`
      );
    },
  });

  return form;
};
export default useFilter;
